import { useEffect, useState } from "react";
import { Plus, Pencil, Search, Package } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { ItemDialog } from "@/components/catalog/ItemDialog";
import { KitDialog } from "@/components/catalog/KitDialog";
import { ExcelTools } from "@/components/catalog/ExcelTools";
import { useApi, errMsg } from "@/lib/api";
import { MODES, eur } from "@/lib/pricing";
import { sectionColor } from "@/lib/constants";

const ItemsTab = ({ items: all, source, kind = "articolo", sections, reload, api }) => {
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");
  const [edit, setEdit] = useState(null);
  const items = all.filter((i) => (i.source || "proprio") === source && (source === "noleggio" || (i.kind || "articolo") === kind));
  const rent = source === "noleggio";
  const shown = items.filter((i) => (cat === "all" || i.category === cat) && i.name.toLowerCase().includes(search.toLowerCase()));
  const del = async (i) => { try { await api.delete(`/catalog/${i.id}`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca articolo" className="pl-9" data-testid="catalog-search-input" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="sm:w-56" data-testid="catalog-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le sezioni</SelectItem>
            {sections.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={() => setEdit({ source, kind })} data-testid="catalog-new-item-btn"><Plus className="h-4 w-4 mr-1" />{rent ? "Nuovo articolo a noleggio" : kind === "kit" ? "Nuovo kit" : "Nuovo articolo"}</Button>
      </div>
      <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-x-auto">
        <table className="w-full text-left" data-testid="catalog-table">
          <thead><tr className="micro-label border-b border-slate-800">
            <th className="py-2 px-3">Sezione</th><th className="py-2 px-3">Articolo</th><th className="py-2 px-3 hidden sm:table-cell">Calcolo</th><th className="py-2 px-3 text-right whitespace-nowrap">{rent ? "Vendita/gg" : "Prezzo/gg"}</th><th className="py-2 px-3 text-right whitespace-nowrap">{rent ? "Costo noleggio" : "Costo"}</th><th />
          </tr></thead>
          <tbody>
            {shown.map((i) => (
              <tr key={i.id} className="border-b border-slate-800/70 hover:bg-slate-800/30" data-testid={`catalog-row-${i.id}`}>
                <td className="py-2.5 px-3"><span className="text-[10px] font-mono px-2 py-0.5 rounded whitespace-nowrap" style={{ background: `${sectionColor(i.category)}22`, color: sectionColor(i.category) }}>{i.category}</span></td>
                <td className="py-2.5 px-3"><div className="font-medium text-sm">{i.name}</div>{rent && i.supplier && <div className="text-xs text-cyan-300">Fornitore: {i.supplier}</div>}{i.details && <div className="text-xs text-slate-500 line-clamp-1">{i.details.replace(/\n/g, " · ")}</div>}</td>
                <td className="py-2.5 px-3 font-mono text-xs text-slate-400 whitespace-nowrap hidden sm:table-cell">{MODES[i.mode]}</td>
                <td className="py-2.5 px-3 text-right font-mono text-sm">{eur(i.price)}</td>
                <td className="py-2.5 px-3 text-right font-mono text-sm text-cyan-300" data-testid={`catalog-cost-${i.id}`}>{+i.cost > 0 ? <>{eur(i.cost)}<div className="text-[10px] text-slate-500">{MODES[i.cost_mode || i.mode]}</div></> : <span className="text-slate-600">—</span>}</td>
                <td className="py-2.5 px-3"><div className="flex justify-end gap-1">
                  <Button size="icon" variant="ghost" onClick={() => setEdit(i)} data-testid={`catalog-edit-${i.id}`}><Pencil className="h-4 w-4" /></Button>
                  <ConfirmDelete onConfirm={() => del(i)} title={`Eliminare "${i.name}"?`} testId={`catalog-del-${i.id}`}><span className="sr-only">Elimina</span>×</ConfirmDelete>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
        {shown.length === 0 && <p className="p-8 text-center text-sm text-slate-400">Nessun articolo trovato.</p>}
      </div>
      <ItemDialog item={edit} sections={sections} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />
    </div>
  );
};

const KitsTab = ({ kits, items, reload, api }) => {
  const [edit, setEdit] = useState(null);
  const byId = Object.fromEntries(items.map((i) => [i.id, i]));
  const del = async (k) => { try { await api.delete(`/kits/${k.id}`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-400">I pacchetti inseriscono più articoli in un colpo solo nel preventivo.</p>
        <Button onClick={() => setEdit({})} data-testid="kit-new-btn"><Plus className="h-4 w-4 mr-1" />Nuovo pacchetto</Button>
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {kits.map((k) => (
          <div key={k.id} className="bg-slate-900 border border-slate-800 rounded-lg p-4 hover:border-amber-500/40 transition-colors" data-testid={`kit-card-${k.id}`}>
            <div className="flex items-start gap-2">
              <Package className="h-5 w-5 text-amber-400 mt-0.5" />
              <div className="flex-1 min-w-0"><div className="font-semibold">{k.name}</div><div className="text-xs text-slate-400">{k.description}</div></div>
              <Button size="icon" variant="ghost" onClick={() => setEdit(k)} data-testid={`kit-edit-${k.id}`}><Pencil className="h-4 w-4" /></Button>
              <ConfirmDelete onConfirm={() => del(k)} title={`Eliminare il pacchetto "${k.name}"?`} testId={`kit-del-${k.id}`}>×</ConfirmDelete>
            </div>
            <ul className="mt-3 space-y-1 text-sm">
              {k.items.map((ki) => <li key={ki.item_id} className="flex gap-2"><span className="font-mono text-amber-300 w-8">{ki.qty}×</span><span className="truncate text-slate-300">{byId[ki.item_id]?.name || "Articolo rimosso"}</span></li>)}
            </ul>
          </div>
        ))}
      </div>
      <KitDialog kit={edit} items={items} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />
    </div>
  );
};

export default function CatalogPage() {
  const api = useApi();
  const [d, setD] = useState(null);
  const reload = () => Promise.all(["/catalog", "/kits", "/settings"].map((p) => api.get(p)))
    .then(([c, k, s]) => setD({ items: c.data, kits: k.data, sections: s.data.sections }))
    .catch((e) => toast.error(errMsg(e)));
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return null;
  return (
    <div className="space-y-6 rise" data-testid="catalog-page">
      <div>
        <p className="micro-label">Listino</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">Catalogo & Kit</h1>
        <p className="text-slate-400 mt-2 text-sm">Prezzo giornaliero per articolo. Calcolo: <span className="font-mono text-amber-300">√ giorni</span> per le attrezzature, <span className="font-mono text-amber-300">× giorni</span> per i tecnici, <span className="font-mono text-amber-300">fisso</span> per trasporti e simili. Il <span className="text-cyan-300">costo</span> è interno: serve per la marginalità e non compare mai nel PDF.</p>
      </div>
      <ExcelTools onImported={reload} />
      <Tabs defaultValue="items">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="items" data-testid="tab-items">Articoli propri ({d.items.filter((i) => (i.source || "proprio") === "proprio" && (i.kind || "articolo") === "articolo").length})</TabsTrigger>
          <TabsTrigger value="rent" data-testid="tab-rent">Materiale a noleggio ({d.items.filter((i) => i.source === "noleggio").length})</TabsTrigger>
          <TabsTrigger value="kit-items" data-testid="tab-kit-items">Kit ({d.items.filter((i) => (i.source || "proprio") === "proprio" && i.kind === "kit").length})</TabsTrigger>
          <TabsTrigger value="kits" data-testid="tab-kits">Pacchetti ({d.kits.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="items" className="mt-4"><ItemsTab items={d.items} source="proprio" sections={d.sections} reload={reload} api={api} /></TabsContent>
        <TabsContent value="rent" className="mt-4"><ItemsTab items={d.items} source="noleggio" sections={d.sections} reload={reload} api={api} /></TabsContent>
        <TabsContent value="kit-items" className="mt-4"><ItemsTab items={d.items} source="proprio" kind="kit" sections={d.sections} reload={reload} api={api} /></TabsContent>
        <TabsContent value="kits" className="mt-4"><KitsTab kits={d.kits} items={d.items} reload={reload} api={api} /></TabsContent>
      </Tabs>
    </div>
  );
}
